import { useState, useEffect, useMemo, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { X, ClipboardList } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * MaterialUsageDialog — Shared "Material Usage" dialog for entering balance
 * material data after a work-order's machines have finished. Mirrors the
 * legacy EmployeeTimesheet dialog UX (green header, countdown, summary,
 * Save & Complete). Used by:
 *   • Core: standalone /material-usage page (no clock-in/out)
 *   • Pro:  EmployeeTimesheet clock-out flow (future refactor)
 *
 * Props:
 *   open          : boolean
 *   onClose       : () => void              (Back/X — no save)
 *   onSuccess     : () => void              (after successful Save & Complete)
 *   workOrder     : full WO object (with header, processes)
 *   machineKey    : "process1" | "process2a" | "process2b" | "process3"
 *   machineId     : "M001"     | "M002A"      | "M002B"      | "M003"
 *   machineName   : human label
 *   employeeName  : operator name (recorded in audit fields)
 *   employeeId    : optional employee id
 */

const PROCESS_BADGE = {
  M001:  { label: "Corrugation",        classes: "bg-orange-100 text-orange-700" },
  M002A: { label: "Sheet Cutting (P2A)", classes: "bg-blue-100 text-blue-700" },
  M002B: { label: "Paper Cutting (P2B)", classes: "bg-cyan-100 text-cyan-700" },
  M003:  { label: "Sheet Pasting",      classes: "bg-purple-100 text-purple-700" },
};

const extractGSMFromRmName = (rmName) => {
  if (!rmName) return 0;
  const match = rmName.match(/[A-Za-z]+(\d{3})\d{2}/);
  return match ? parseInt(match[1]) : 0;
};

export default function MaterialUsageDialog({
  open, onClose, onSuccess,
  workOrder, machineKey, machineId, machineName,
  employeeName, employeeId,
}) {
  const woNum = workOrder?.header?.work_order_number || workOrder?.wo_number || "";
  const [loading, setLoading] = useState(false);
  const [countdown, setCountdown] = useState(120);
  const [productionCounts, setProductionCounts] = useState({
    sheetCounting: "", paperCounting: "", boardCountingP3: "",
  });
  const [materialTrackingData, setMaterialTrackingData] = useState([]);
  const [corrugatedRollsCreated, setCorrugatedRollsCreated] = useState("");
  const [p2bReelInfo, setP2bReelInfo] = useState(null);

  // Build the WO reel set for the selected machine + load inventory for matching reels
  const loadWorkOrderReels = useCallback(async () => {
    try {
      const reelIds = [];
      const reelMeta = [];
      const processes = workOrder?.processes || {};

      if (machineId === "M001" && processes.process1?.rows) {
        let baseCount = 0, fluteCount = 0;
        processes.process1.rows.forEach((row) => {
          if (row.base_reel_id) {
            baseCount++;
            reelIds.push(row.base_reel_id);
            reelMeta.push({ id: row.base_reel_id, label: `Base Reel #${baseCount}`, source: "corrugation", weight: row.base_weight, reelType: "base" });
          }
          if (row.flute_reel_id) {
            fluteCount++;
            reelIds.push(row.flute_reel_id);
            reelMeta.push({ id: row.flute_reel_id, label: `Flute Reel #${fluteCount}`, source: "corrugation", weight: row.flute_weight, reelType: "flute" });
          }
        });
      }

      if (machineId === "M002A" && processes.process2a) {
        const arr = processes.process2a.reels;
        if (Array.isArray(arr)) {
          arr.forEach((r, idx) => {
            if (r.reel_no) { reelIds.push(r.reel_no); reelMeta.push({ id: r.reel_no, label: `Sheet Cutting Reel #${idx + 1}`, source: "sheet_cutting", weight: r.reel_weight, size: r.reel_size }); }
          });
        } else if (processes.process2a.reel_no) {
          reelIds.push(processes.process2a.reel_no);
          reelMeta.push({ id: processes.process2a.reel_no, label: "Sheet Cutting Reel #1", source: "sheet_cutting", weight: processes.process2a.reel_weight, size: processes.process2a.reel_size });
        }
      }

      if (machineId === "M002B" && processes.process2b) {
        const arr = processes.process2b.reels;
        if (Array.isArray(arr)) {
          arr.forEach((r, idx) => {
            if (r.reel_no) { reelIds.push(r.reel_no); reelMeta.push({ id: r.reel_no, label: `Paper Cutting Reel #${idx + 1}`, source: "paper_cutting", weight: r.reel_weight, size: r.reel_size }); }
          });
        } else if (processes.process2b.reel_no) {
          reelIds.push(processes.process2b.reel_no);
          reelMeta.push({ id: processes.process2b.reel_no, label: "Paper Cutting Reel #1", source: "paper_cutting", weight: processes.process2b.reel_weight, size: processes.process2b.reel_size });
        }
      }

      if (machineId === "M003" && processes.process3) {
        const added = new Set();
        const tps = processes.process3.top_paper_types;
        if (Array.isArray(tps)) {
          tps.forEach((paper, idx) => {
            if (paper.stock_id && !added.has(paper.stock_id)) {
              added.add(paper.stock_id);
              reelIds.push(paper.stock_id);
              reelMeta.push({ id: paper.stock_id, label: `${paper.top_paper_type || "Top Paper"} #${idx + 1}`, source: "sheet_pasting", qty: paper.qty, isPrintedPaper: true });
            }
          });
        }
        if (processes.process3.stock_id && !added.has(processes.process3.stock_id)) {
          added.add(processes.process3.stock_id);
          reelIds.push(processes.process3.stock_id);
          reelMeta.push({ id: processes.process3.stock_id, label: processes.process3.top_paper_type || "Top Paper", source: "sheet_pasting", qty: processes.process3.sheet_counting, isPrintedPaper: true });
        }
      }

      let matchedReels = [];
      if (reelIds.length > 0) {
        const invResp = await axios.get(`${API}/inventory`);
        matchedReels = invResp.data.filter((item) => {
          const direct = reelIds.includes(item.reel_id) || reelIds.includes(item.rm_name);
          const split = item.is_split_stock && reelIds.includes(item.parent_reel_id);
          return (direct || split) && item.status === "available";
        });
      }

      let initialRows = [];
      if (machineId !== "M002B") {
        initialRows = reelMeta.map((meta, idx) => {
          const reel = matchedReels.find((r) => r.reel_id === meta.id || r.rm_name === meta.id);
          return {
            id: Date.now() + idx,
            reelId: reel ? reel.reel_id : meta.id,
            label: meta.label,
            source: meta.source,
            reelType: meta.reelType || null,
            usedWeight: "",
            balanceWeight: "",
            assignedWeight: meta.weight || reel?.reel_weight || "",
            assignedQty: meta.qty || reel?.qty || "",
            usedQty: "",
            balanceQty: "",
            reelData: reel || null,
            isAssigned: true,
            isPrintedPaper: !!meta.isPrintedPaper,
          };
        });
      }
      setMaterialTrackingData(initialRows);

      if (machineId === "M002B") {
        try {
          const woResponse = await axios.get(`${API}/work-orders/by-number/${woNum}`);
          const wo = woResponse.data;
          const p2b = wo.processes?.process2b || {};
          const header = wo.header || {};
          const reelSize = parseFloat(p2b.reels?.[0]?.reel_size || header.reel_size) || 0;
          const cuttingSize = parseFloat(p2b.cutting_size || header.cutting_size) || 0;
          const reelNo = p2b.reels?.[0]?.reel_no || "";
          if (reelNo) {
            const invResponse = await axios.get(`${API}/inventory`);
            const invItem = invResponse.data.find((it) => it.reel_id === reelNo);
            if (invItem) {
              const gsm = invItem.page_no ? parseFloat(invItem.page_no) : extractGSMFromRmName(invItem.rm_name);
              const originalWeight = parseFloat(invItem.reel_weight) || 0;
              setP2bReelInfo({ gsm, reelSize, cuttingSize, originalWeight, reelId: reelNo });
            }
          }
        } catch (err) {
          console.error("Error fetching P2B reel info:", err);
        }
      }
    } catch (e) {
      console.error("Failed to load WO reels:", e);
      setMaterialTrackingData([]);
    }
  }, [workOrder, machineId, woNum]);

  // Reset state when dialog opens
  useEffect(() => {
    if (!open) return;
    setCountdown(120);
    setProductionCounts({ sheetCounting: "", paperCounting: "", boardCountingP3: "" });
    setCorrugatedRollsCreated("");
    setP2bReelInfo(null);
    loadWorkOrderReels();
  }, [open, machineId, woNum, loadWorkOrderReels]);

  // Countdown timer (visual hint)
  useEffect(() => {
    if (!open) return;
    const t = setInterval(() => setCountdown((c) => (c > 0 ? c - 1 : 0)), 1000);
    return () => clearInterval(t);
  }, [open]);

  // P2B auto-calculation
  const p2bCalc = useMemo(() => {
    if (!p2bReelInfo || !productionCounts.paperCounting) return null;
    const { gsm, reelSize, cuttingSize, originalWeight } = p2bReelInfo;
    if (!gsm || !reelSize || !cuttingSize) return null;
    const paperCount = parseInt(productionCounts.paperCounting) || 0;
    if (paperCount <= 0) return null;
    const paperWeightKg = (reelSize * cuttingSize * gsm) / 1550 / 1000;
    const totalPapersPossible = Math.floor(originalWeight / paperWeightKg);
    const usedWeightKg = paperCount * paperWeightKg;
    const balancePapers = Math.max(0, totalPapersPossible - paperCount);
    const balanceWeightKg = Math.max(0, balancePapers * paperWeightKg);
    return {
      paperWeightKg: paperWeightKg.toFixed(4),
      totalPapersPossible,
      usedWeightKg: usedWeightKg.toFixed(2),
      balancePapers,
      balanceWeightKg: balanceWeightKg.toFixed(2),
    };
  }, [p2bReelInfo, productionCounts.paperCounting]);

  const updateRow = (id, field, value) => {
    setMaterialTrackingData((rows) =>
      rows.map((row) => {
        if (row.id !== id) return row;
        const next = { ...row, [field]: value };
        if (field === "balanceWeight") {
          const assigned = parseFloat(row.assignedWeight) || parseFloat(row.reelData?.reel_weight) || 0;
          const balance = parseFloat(value) || 0;
          if (assigned > 0) next.usedWeight = Math.max(0, assigned - balance).toFixed(2);
        }
        if (field === "usedQty") {
          const assigned = parseFloat(row.assignedQty) || parseFloat(row.reelData?.qty) || 0;
          const used = parseFloat(value) || 0;
          if (assigned > 0) next.balanceQty = Math.max(0, assigned - used).toString();
        }
        return next;
      })
    );
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      const operatorTag = employeeName || "Operator";
      const auditTag = `${operatorTag}${employeeId ? ` (${employeeId})` : ""}`;

      // 1) Material tracking rows → bit-reel / split-stock / inventory PUT
      for (const row of materialTrackingData) {
        if (row.isPrintedPaper) {
          if (!row.reelId || !row.reelData) continue;
          const usedQty = parseFloat(row.usedQty) || 0;
          const balanceQty = parseFloat(row.balanceQty) || 0;
          if (balanceQty > 0) {
            await axios.post(`${API}/inventory/split-stock`, {
              original_item_id: row.reelData.id,
              used_qty: usedQty,
              balance_qty: balanceQty,
              used_comment: `Used in ${woNum} on ${machineName} by ${auditTag}`,
              work_order_number: woNum,
            });
          } else if (usedQty > 0) {
            await axios.put(`${API}/inventory/${row.reelData.id}`, {
              status: "stock_used",
              used_comment: `Fully used in ${woNum} on ${machineName} by ${auditTag}`,
              used_in_work_order: woNum,
            });
          }
        } else {
          if (!row.reelId || !row.usedWeight || !row.reelData) continue;
          const usedWeight = parseFloat(row.usedWeight) || 0;
          const balanceWeight = parseFloat(row.balanceWeight) || 0;
          if (balanceWeight > 0) {
            await axios.post(`${API}/inventory/bit-reel`, {
              original_item_id: row.reelData.id,
              used_weight: usedWeight,
              balance_weight: balanceWeight,
              used_comment: `Used in ${woNum} on ${machineName} by ${auditTag}`,
              work_order_number: woNum,
              reel_type: row.reelType || null,
            });
          } else {
            await axios.put(`${API}/inventory/${row.reelData.id}`, {
              status: "stock_used",
              used_comment: `Fully used in ${woNum} on ${machineName} by ${auditTag}`,
              used_in_work_order: woNum,
            });
          }
        }
      }

      // 2) Save tracked reel IDs
      const trackedReelIds = materialTrackingData
        .filter((r) => r.reelId && (r.usedWeight || r.usedQty))
        .map((r) => r.reelId);
      if (trackedReelIds.length > 0) {
        await axios.patch(`${API}/work-orders/${woNum}/update-material-tracked-reels`, trackedReelIds);
      }

      // 3) M001 Corrugated Rolls
      if (machineId === "M001" && corrugatedRollsCreated) {
        const rollsCount = parseInt(corrugatedRollsCreated) || 0;
        if (rollsCount > 0) {
          const parentReels = materialTrackingData.filter((r) => r.reelId).map((r) => r.reelId);
          await axios.patch(`${API}/work-orders/${woNum}/update-corrugated-rolls`, {
            corrugated_rolls_created: rollsCount,
            employee_name: auditTag,
            parent_reels: parentReels,
          });
        }
      }

      // 4) M001 base/flute aggregates
      if (machineId === "M001" && materialTrackingData.length > 0) {
        let usedBase = 0, usedFlute = 0, balBase = 0, balFlute = 0;
        materialTrackingData.forEach((row) => {
          const u = parseFloat(row.usedWeight) || 0, b = parseFloat(row.balanceWeight) || 0;
          if (row.reelType === "base") { usedBase += u; balBase += b; }
          else if (row.reelType === "flute") { usedFlute += u; balFlute += b; }
        });
        await axios.patch(`${API}/work-orders/${woNum}/update-actual-counts`, {
          process: "process1",
          actual_used_base_weight: usedBase,
          actual_used_flute_weight: usedFlute,
          balance_base_weight: balBase,
          balance_flute_weight: balFlute,
          employee_name: auditTag,
        });
      }

      // 5) Per-machine production counts
      if (machineId === "M002A" && productionCounts.sheetCounting) {
        await axios.patch(`${API}/work-orders/${woNum}/update-actual-counts`, {
          process: "process2a",
          actual_sheet_counting: parseInt(productionCounts.sheetCounting) || 0,
          employee_name: auditTag,
        });
      }
      if (machineId === "M002B" && productionCounts.paperCounting) {
        await axios.patch(`${API}/work-orders/${woNum}/update-actual-counts`, {
          process: "process2b",
          actual_paper_counting: parseInt(productionCounts.paperCounting) || 0,
          employee_name: auditTag,
        });
        if (p2bReelInfo && p2bCalc) {
          const balanceWeight = parseFloat(p2bCalc.balanceWeightKg) || 0;
          const usedWeight = parseFloat(p2bCalc.usedWeightKg) || 0;
          const invResponse = await axios.get(`${API}/inventory`);
          const originalReel = invResponse.data.find((it) => it.reel_id === p2bReelInfo.reelId);
          if (originalReel) {
            if (balanceWeight > 0) {
              await axios.post(`${API}/inventory/bit-reel`, {
                original_item_id: originalReel.id,
                used_weight: usedWeight,
                balance_weight: balanceWeight,
                used_comment: `Paper cutting in ${woNum} - ${productionCounts.paperCounting} papers (auto) by ${auditTag}`,
                work_order_number: woNum,
                reel_type: null,
              });
            } else {
              await axios.post(`${API}/inventory/use?reel_id=${p2bReelInfo.reelId}&work_order_number=${encodeURIComponent(woNum)}`);
            }
          }
        }
      }
      if (machineId === "M003" && productionCounts.boardCountingP3) {
        await axios.patch(`${API}/work-orders/${woNum}/update-actual-counts`, {
          process: "process3",
          actual_board_counting: parseInt(productionCounts.boardCountingP3) || 0,
          employee_name: auditTag,
        });
      }

      // 6) Mark this process as complete (auto-completes WO if all done)
      await axios.patch(`${API}/work-orders/${encodeURIComponent(woNum)}/complete-process`, null, {
        params: {
          process_name: machineKey,
          ...(employeeId ? { employee_id: employeeId } : {}),
          employee_name: auditTag,
        },
      });

      toast.success("Material usage saved!");
      onSuccess?.();
      onClose?.();
    } catch (e) {
      console.error("Save failed", e);
      toast.error(e?.response?.data?.detail || "Failed to save material usage");
    } finally {
      setLoading(false);
    }
  };

  if (!open) return null;

  const totalMaterials = materialTrackingData.filter((r) => r.reelId).length;
  const bitReelsToCreate = materialTrackingData.filter((r) => parseFloat(r.balanceWeight) > 0 && !r.isPrintedPaper).length;
  const balanceStock = materialTrackingData.filter((r) => parseFloat(r.balanceQty) > 0 && r.isPrintedPaper).length;
  const badge = PROCESS_BADGE[machineId] || { label: machineName, classes: "bg-gray-100 text-gray-700" };

  return (
    <div className="fixed inset-0 bg-black/80 flex items-end sm:items-center justify-center z-50">
      <div className="w-full sm:max-w-lg bg-white rounded-t-2xl sm:rounded-2xl max-h-[95vh] sm:max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="bg-gradient-to-r from-emerald-500 to-teal-600 p-4 sm:p-5">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-lg sm:text-xl font-bold text-white">Material Usage</h2>
              <p className="text-emerald-100 text-xs sm:text-sm mt-1">
                WO: {woNum} · {machineName}
              </p>
            </div>
            <div className="flex items-center gap-2">
              <div className={`text-sm font-bold px-2 py-1 rounded-full ${countdown <= 15 ? "bg-red-100 text-red-600 animate-pulse" : "bg-white/20 text-white"}`}
                   data-testid="mu-countdown">
                {Math.floor(countdown / 60)}:{String(countdown % 60).padStart(2, "0")}
              </div>
              <button onClick={onClose}
                      className="p-2 rounded-full bg-white/20 hover:bg-white/30 text-white"
                      data-testid="mu-close">
                <X className="h-5 w-5" />
              </button>
            </div>
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {/* Process badge */}
          <div className="flex justify-center">
            <span className={`px-3 py-1 rounded-full text-xs font-medium ${badge.classes}`}>{badge.label}</span>
          </div>

          {/* Operator (compact, top of body) */}
          {employeeName && (
            <div className="rounded-lg bg-gray-50 border border-gray-200 px-3 py-2 text-xs">
              <div className="text-gray-500 mb-0.5">Operator</div>
              <div className="font-semibold text-gray-900 truncate">{employeeName}</div>
            </div>
          )}

          {/* M001 — Corrugated Rolls Created */}
          {machineId === "M001" && (
            <div className="rounded-xl border-2 border-amber-200 bg-amber-50 overflow-hidden">
              <div className="px-4 py-3 bg-amber-100 flex items-center gap-2">
                <div className="w-8 h-8 rounded-full flex items-center justify-center bg-amber-500 text-white">
                  <ClipboardList className="h-4 w-4" />
                </div>
                <div>
                  <p className="font-semibold text-gray-800 text-sm">Corrugated Rolls Created</p>
                  <p className="text-xs text-gray-500">Enter number of rolls produced</p>
                </div>
              </div>
              <div className="p-4">
                <input
                  type="number" inputMode="numeric" min="0"
                  value={corrugatedRollsCreated}
                  onChange={(e) => setCorrugatedRollsCreated(e.target.value)}
                  placeholder="Enter roll count"
                  className="w-full px-4 py-3 text-lg font-semibold text-center border-2 border-amber-200 rounded-xl focus:border-amber-500 focus:ring-0 focus:outline-none bg-white text-gray-900"
                  data-testid="mu-corrugated-rolls"
                />
              </div>
            </div>
          )}

          {/* M002A — Sheet Counting */}
          {machineId === "M002A" && (
            <div className="rounded-xl border-2 border-blue-200 bg-blue-50 overflow-hidden">
              <div className="px-4 py-3 bg-blue-100 flex items-center gap-2">
                <div className="w-8 h-8 rounded-full flex items-center justify-center bg-blue-500 text-white">
                  <ClipboardList className="h-4 w-4" />
                </div>
                <div>
                  <p className="font-semibold text-gray-800 text-sm">Sheet Counting</p>
                  <p className="text-xs text-gray-500">Enter actual sheets cut</p>
                </div>
              </div>
              <div className="p-4">
                <input
                  type="number" inputMode="numeric" min="0"
                  value={productionCounts.sheetCounting}
                  onChange={(e) => setProductionCounts((p) => ({ ...p, sheetCounting: e.target.value }))}
                  placeholder="Enter sheet count"
                  className="w-full px-4 py-3 text-lg font-semibold text-center border-2 border-blue-200 rounded-xl focus:border-blue-500 focus:ring-0 focus:outline-none bg-white text-gray-900"
                  data-testid="mu-sheet-counting"
                />
              </div>
            </div>
          )}

          {/* M002B — Paper Counting + auto-calc */}
          {machineId === "M002B" && (
            <div className="rounded-xl border-2 border-cyan-200 bg-cyan-50 overflow-hidden">
              <div className="px-4 py-3 bg-cyan-100 flex items-center gap-2">
                <div className="w-8 h-8 rounded-full flex items-center justify-center bg-cyan-500 text-white">
                  <ClipboardList className="h-4 w-4" />
                </div>
                <div>
                  <p className="font-semibold text-gray-800 text-sm">Paper Cutting</p>
                  <p className="text-xs text-gray-500">Enter paper count — weight auto-calculated</p>
                </div>
              </div>
              <div className="p-4 space-y-3">
                <input
                  type="number" inputMode="numeric" min="0"
                  value={productionCounts.paperCounting}
                  onChange={(e) => setProductionCounts((p) => ({ ...p, paperCounting: e.target.value }))}
                  placeholder="Enter paper count"
                  className="w-full px-4 py-3 text-lg font-semibold text-center border-2 border-cyan-200 rounded-xl focus:border-cyan-500 focus:ring-0 focus:outline-none bg-white text-gray-900"
                  data-testid="mu-paper-counting"
                />
                {p2bReelInfo && p2bCalc && (
                  <div className="bg-white rounded-xl border border-cyan-200 p-3 space-y-2 text-xs">
                    <p className="font-semibold text-cyan-700 uppercase tracking-wider">Auto-Calculated</p>
                    <p className="text-gray-500 font-mono">
                      Total papers possible: <span className="text-gray-900 font-bold">{p2bCalc.totalPapersPossible}</span>
                    </p>
                    <div className="flex justify-between items-center bg-cyan-50 rounded-lg p-2">
                      <div>
                        <p className="text-gray-500">Used Weight</p>
                        <p className="text-sm font-bold text-red-600">{p2bCalc.usedWeightKg} kg</p>
                      </div>
                      <div className="text-right">
                        <p className="text-gray-500">Balance Reel Wt</p>
                        <p className="text-sm font-bold text-green-600">{p2bCalc.balanceWeightKg} kg</p>
                      </div>
                    </div>
                    <p className="text-cyan-600 text-center">
                      Bit reel auto-created with {p2bCalc.balanceWeightKg} kg ({p2bCalc.balancePapers} papers)
                    </p>
                  </div>
                )}
                {!p2bReelInfo && (
                  <p className="text-xs text-amber-600 text-center">Reel info not found — check WO reel assignment</p>
                )}
              </div>
            </div>
          )}

          {/* M003 — Board Counting */}
          {machineId === "M003" && (
            <div className="rounded-xl border-2 border-purple-200 bg-purple-50 overflow-hidden">
              <div className="px-4 py-3 bg-purple-100 flex items-center gap-2">
                <div className="w-8 h-8 rounded-full flex items-center justify-center bg-purple-500 text-white">
                  <ClipboardList className="h-4 w-4" />
                </div>
                <div>
                  <p className="font-semibold text-gray-800 text-sm">Board Counting</p>
                  <p className="text-xs text-gray-500">Enter actual boards pasted</p>
                </div>
              </div>
              <div className="p-4">
                <input
                  type="number" inputMode="numeric" min="0"
                  value={productionCounts.boardCountingP3}
                  onChange={(e) => setProductionCounts((p) => ({ ...p, boardCountingP3: e.target.value }))}
                  placeholder="Enter count"
                  className="w-full px-4 py-3 text-lg font-semibold text-center border-2 border-purple-200 rounded-xl focus:border-purple-500 focus:ring-0 focus:outline-none bg-white text-gray-900"
                  data-testid="mu-board-counting"
                />
              </div>
            </div>
          )}

          {/* Material Tracking Rows (skipped for M002B) */}
          {machineId !== "M002B" && materialTrackingData.length > 0 && (
            <div className="space-y-2">
              <p className="text-xs font-semibold text-gray-700 uppercase tracking-wider">Material Tracking</p>
              {materialTrackingData.map((row) => (
                <div key={row.id} className="rounded-xl border-2 border-gray-200 bg-white p-3 space-y-2">
                  <div className="flex justify-between text-xs">
                    <span className="font-semibold text-gray-800">{row.label}</span>
                    <span className="text-gray-500">{row.reelId}</span>
                  </div>
                  {!row.isPrintedPaper ? (
                    <>
                      <div className="text-[10px] text-gray-500">
                        Assigned: <span className="font-bold text-gray-800">{row.assignedWeight || "—"} kg</span>
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <label className="block text-[10px] font-medium text-gray-600 mb-1">Balance (Kg)</label>
                          <input
                            type="number" inputMode="decimal" min="0" step="0.01"
                            value={row.balanceWeight}
                            onChange={(e) => updateRow(row.id, "balanceWeight", e.target.value)}
                            placeholder="0"
                            className="w-full px-3 py-2 text-base font-semibold text-center border-2 border-gray-200 rounded-lg focus:border-emerald-500 focus:ring-0 focus:outline-none text-gray-900 bg-white"
                          />
                        </div>
                        <div>
                          <label className="block text-[10px] font-medium text-gray-600 mb-1">Used (Kg)</label>
                          <div className="w-full px-3 py-2 text-base font-semibold text-center bg-gray-100 rounded-lg text-gray-700">
                            {row.usedWeight || "—"}
                          </div>
                        </div>
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="text-[10px] text-gray-500">
                        Assigned: <span className="font-bold text-gray-800">{row.assignedQty || "—"} Nos</span>
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <label className="block text-[10px] font-medium text-gray-600 mb-1">Used (Nos)</label>
                          <input
                            type="number" inputMode="numeric" min="0"
                            value={row.usedQty}
                            onChange={(e) => updateRow(row.id, "usedQty", e.target.value)}
                            placeholder="0"
                            className="w-full px-3 py-2 text-base font-semibold text-center border-2 border-gray-200 rounded-lg focus:border-emerald-500 focus:ring-0 focus:outline-none text-gray-900 bg-white"
                          />
                        </div>
                        <div>
                          <label className="block text-[10px] font-medium text-gray-600 mb-1">Balance (Nos)</label>
                          <div className="w-full px-3 py-2 text-base font-semibold text-center bg-gray-100 rounded-lg text-gray-700">
                            {row.balanceQty || "—"}
                          </div>
                        </div>
                      </div>
                    </>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Summary Card */}
          <div className="p-4 bg-emerald-50 border border-emerald-200 rounded-xl">
            <div className="flex items-center justify-between text-sm">
              <span className="text-emerald-700">Total Materials</span>
              <span className="font-bold text-emerald-800" data-testid="mu-total-materials">{totalMaterials}</span>
            </div>
            <div className="flex items-center justify-between text-sm mt-1">
              <span className="text-emerald-700">Bit Reels to Create</span>
              <span className="font-bold text-emerald-800" data-testid="mu-bit-reels">{bitReelsToCreate}</span>
            </div>
            <div className="flex items-center justify-between text-sm mt-1">
              <span className="text-emerald-700">Balance Stock</span>
              <span className="font-bold text-emerald-800" data-testid="mu-balance-stock">{balanceStock}</span>
            </div>
            {machineId === "M001" && corrugatedRollsCreated && (
              <div className="flex items-center justify-between text-sm mt-1 pt-1 border-t border-emerald-200">
                <span className="text-amber-700">Corrugated Rolls</span>
                <span className="font-bold text-amber-800">{corrugatedRollsCreated}</span>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="p-4 bg-white border-t border-gray-200 space-y-2">
          <Button
            onClick={handleSave}
            disabled={loading}
            className="w-full h-12 bg-emerald-600 hover:bg-emerald-700 text-white text-base font-semibold rounded-xl"
            data-testid="mu-save-complete"
          >
            {loading ? "Saving..." : "✓ Save & Complete"}
          </Button>
          <div className="grid grid-cols-2 gap-2">
            <Button onClick={onClose} variant="outline"
                    className="h-10 rounded-xl text-sm text-gray-700 border-gray-300 hover:bg-gray-100"
                    data-testid="mu-skip">
              Skip (No Count)
            </Button>
            <Button onClick={onClose} variant="ghost"
                    className="h-10 rounded-xl text-sm text-gray-500"
                    data-testid="mu-back">
              ← Back
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
